import { Component, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AssetLogRecord,
  AssetLogsParams,
  AssetLogsService,
  UpdatedField,
  canViewAssetLogs,
  formatEndDateToISO,
  formatStartDateToISO
} from 'src/app/service/asset-logs.service';

export type AssetTypeFilter = 'All' | 'Inventory' | 'Workstation';
export type DatePreset = 'all' | 'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth' | 'custom';

@Component({
  selector: 'app-asset-logs',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe],
  templateUrl: './asset-logs.component.html',
  styleUrls: ['./asset-logs.component.css']
})
export class AssetLogsComponent implements OnInit {
  readonly Math = Math;
  records: AssetLogRecord[] = [];
  totalCounts = 0;
  totalPages = 1;
  page = 1;
  limit = 20;
  limitOptions = [10, 20, 50, 100];

  isLoading = false;
  isUnauthorized = false;
  errorMessage = '';

  // Filters
  assetTypeFilter: AssetTypeFilter = 'All';
  selectedDatePreset: DatePreset = 'all';
  startDateInput: string = ''; // YYYY-MM-DD
  endDateInput: string = '';   // YYYY-MM-DD
  searchKeyword: string = '';

  // Row expansion tracking
  expandedRowIds = new Set<string>();

  // Lightbox preview
  selectedPreviewImage: string | null = null;

  // Selected record for details slide-over / modal
  selectedRecordForDetails: AssetLogRecord | null = null;

  constructor(
    public assetLogsService: AssetLogsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (!canViewAssetLogs()) {
      this.isUnauthorized = true;
      this.errorMessage = 'Not Authorized To View Asset Logs!';
      return;
    }

    this.fetchLogs();
  }

  fetchLogs(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.isUnauthorized = false;

    const params: AssetLogsParams = {
      page: this.page,
      limit: this.limit
    };

    if (this.assetTypeFilter !== 'All') {
      params.assetType = this.assetTypeFilter;
    }

    if (this.startDateInput) {
      params.startDate = formatStartDateToISO(this.startDateInput);
    }

    if (this.endDateInput) {
      params.endDate = formatEndDateToISO(this.endDateInput);
    }

    this.assetLogsService.getAssetLogs(params).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res?.data) {
          this.records = res.data.records || [];
          this.totalCounts = res.data.totalCounts || 0;
          this.totalPages = Math.max(1, Math.ceil(this.totalCounts / this.limit));
        }
      },
      error: (err) => {
        this.isLoading = false;
        if (err?.status === 400) {
          this.isUnauthorized = true;
          this.errorMessage = err?.error?.message || 'Not Authorized To View Asset Logs!';
        } else {
          this.errorMessage = 'Failed to fetch asset activity logs. Please try again.';
        }
      }
    });
  }

  // Filter changes MUST reset page to 1
  onAssetTypeChange(type: AssetTypeFilter): void {
    if (this.assetTypeFilter === type) return;
    this.assetTypeFilter = type;
    this.page = 1;
    this.fetchLogs();
  }

  onLimitChange(newLimit: number): void {
    this.limit = newLimit;
    this.page = 1;
    this.fetchLogs();
  }

  onPageChange(newPage: number): void {
    if (newPage < 1 || newPage > this.totalPages || newPage === this.page) return;
    this.page = newPage;
    this.fetchLogs();
  }

  onDatePresetChange(preset: DatePreset): void {
    this.selectedDatePreset = preset;
    const now = new Date();

    switch (preset) {
      case 'all':
        this.startDateInput = '';
        this.endDateInput = '';
        break;

      case 'today': {
        const todayStr = this.formatDateToYMD(now);
        this.startDateInput = todayStr;
        this.endDateInput = todayStr;
        break;
      }

      case 'yesterday': {
        const y = new Date(now);
        y.setDate(y.getDate() - 1);
        const yStr = this.formatDateToYMD(y);
        this.startDateInput = yStr;
        this.endDateInput = yStr;
        break;
      }

      case 'last7': {
        const start = new Date(now);
        start.setDate(start.getDate() - 6);
        this.startDateInput = this.formatDateToYMD(start);
        this.endDateInput = this.formatDateToYMD(now);
        break;
      }

      case 'last30': {
        const start = new Date(now);
        start.setDate(start.getDate() - 29);
        this.startDateInput = this.formatDateToYMD(start);
        this.endDateInput = this.formatDateToYMD(now);
        break;
      }

      case 'thisMonth': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        this.startDateInput = this.formatDateToYMD(start);
        this.endDateInput = this.formatDateToYMD(now);
        break;
      }

      case 'custom':
        // Keep current custom inputs or allow user to change
        return;
    }

    this.page = 1;
    this.fetchLogs();
  }

  onCustomDateChange(): void {
    this.selectedDatePreset = 'custom';
    this.page = 1;
    this.fetchLogs();
  }

  resetFilters(): void {
    this.assetTypeFilter = 'All';
    this.selectedDatePreset = 'all';
    this.startDateInput = '';
    this.endDateInput = '';
    this.searchKeyword = '';
    this.page = 1;
    this.fetchLogs();
  }

  toggleRowExpand(id: string): void {
    if (this.expandedRowIds.has(id)) {
      this.expandedRowIds.delete(id);
    } else {
      this.expandedRowIds.add(id);
    }
  }

  isRowExpanded(id: string): boolean {
    return this.expandedRowIds.has(id);
  }

  openRecordDetails(record: AssetLogRecord): void {
    this.selectedRecordForDetails = record;
  }

  closeRecordDetails(): void {
    this.selectedRecordForDetails = null;
  }

  openImagePreview(imgUrl: string): void {
    this.selectedPreviewImage = imgUrl;
  }

  closeImagePreview(): void {
    this.selectedPreviewImage = null;
  }

  getActionBadgeClass(action: string): string {
    const act = (action || '').toLowerCase();
    if (act.includes('create') || act.includes('approved')) return 'badge-success';
    if (act.includes('update') || act.includes('submitted')) return 'badge-info';
    if (act.includes('assign') && !act.includes('unassign')) return 'badge-teal';
    if (act.includes('extend') || act.includes('on hold')) return 'badge-warning';
    if (act.includes('forward') || act.includes('raised')) return 'badge-purple';
    if (act.includes('delete') || act.includes('reject') || act.includes('removed')) return 'badge-danger';
    if (act.includes('unassign') || act.includes('return')) return 'badge-indigo';
    return 'badge-default';
  }

  getUpdatedFields(record: AssetLogRecord): UpdatedField[] {
    const fields = record?.meta?.updatedFields;
    return Array.isArray(fields) ? fields : [];
  }

  getImages(record: AssetLogRecord): string[] {
    const imgs = record?.meta?.images;
    return Array.isArray(imgs) ? imgs : [];
  }

  hasExtraMeta(record: AssetLogRecord): boolean {
    const entries = this.assetLogsService.getGenericMetaEntries(record?.meta, record?.action);
    return entries.length > 0;
  }

  formatValue(val: any): string {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'object') {
      try {
        return JSON.stringify(val);
      } catch {
        return String(val);
      }
    }
    return String(val);
  }

  getInitials(name: string): string {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  private formatDateToYMD(d: Date): string {
    const pad = (n: number) => ('00' + n).slice(-2);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  // Client-side quick filter on current page if search term entered
  get filteredRecords(): AssetLogRecord[] {
    if (!this.searchKeyword.trim()) {
      return this.records;
    }
    const q = this.searchKeyword.toLowerCase().trim();
    return this.records.filter((r) =>
      (r.assetName || '').toLowerCase().includes(q) ||
      (r.assetCode || '').toLowerCase().includes(q) ||
      (r.actorName || '').toLowerCase().includes(q) ||
      (r.targetUserName || '').toLowerCase().includes(q) ||
      (r.action || '').toLowerCase().includes(q) ||
      this.assetLogsService.buildMetaSummary(r).toLowerCase().includes(q)
    );
  }
}
